<script>
import { computed, ref } from 'vue-demi';
import IconClear from '~icons/tw/input-clear';
import IconSearch from '~icons/tw/input-search';
import { searchInputSizes, defaultSearchInputSize, searchInputVariants, defaultSearchInputVariant } from './consts';
import optionPropValidator from '../../../helpers/optionPropValidator';

const sizeClassesMap = {
  [searchInputSizes.sm]: 'h-[var(--token-c-input-size-small)]',
  [searchInputSizes.md]: 'h-[var(--token-c-input-size-medium)]',
  [searchInputSizes.lg]: 'h-[var(--token-c-input-size-large)]',
};

const variantClassesMap = {
  [searchInputVariants.outline]: {
    background: 'bg-[color:var(--token-c-input-color-background-outline-default)]',
    border: {
      default: 'border-[color:var(--token-c-input-color-border-outline-default)]',
      disabled: 'border-[color:var(--token-c-input-color-border-outline-disabled)]',
      focus: 'border-[color:var(--token-c-input-color-border-outline-focus)]',
      hover: 'hover:border-[color:var(--token-c-input-color-border-outline-hover)]',
      invalid: 'border-[color:var(--token-c-input-color-border-outline-invalid)]',
    },
  },
  [searchInputVariants.filled]: {
    background: 'bg-[color:var(--token-c-input-color-background-filled-default)]',
    border: {
      default: 'border-[color:var(--token-c-input-color-border-filled-default)]',
      disabled: 'border-[color:var(--token-c-input-color-border-filled-disabled)]',
      focus: 'border-[color:var(--token-c-input-color-border-filled-focus)]',
      hover: 'hover:border-[color:var(--token-c-input-color-border-filled-hover)]',
      invalid: 'border-[color:var(--token-c-input-color-border-filled-invalid)]',
    },
  },
};

export default {
  name: 'FSearchInput',
  components: { IconClear, IconSearch },
  inheritAttrs: false,
  model: {
    prop: 'modelValue',
    event: 'input',
  },
  props: {
    modelValue: {
      type: String,
      default: '',
    },
    size: {
      type: String,
      default: defaultSearchInputSize,
      validator: optionPropValidator(searchInputSizes, 'size'),
    },
    variant: {
      type: String,
      default: defaultSearchInputVariant,
      validator: optionPropValidator(searchInputVariants, 'variant'),
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    invalid: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const inputRef = ref(null);
    const isFocused = ref(false);

    const variant = computed(() => variantClassesMap[props.variant]);
    const sizeClass = computed(() => sizeClassesMap[props.size]);

    const borderClasses = computed(() => {
      const { border } = variant.value;
      if (props.disabled) {
        return border.disabled;
      }
      if (isFocused.value) {
        return border.focus;
      }
      if (props.invalid) {
        return border.invalid;
      }
      return [border.default, border.hover];
    });

    const wrapperClasses = computed(() => [variant.value.background, borderClasses.value]);

    const inputClasses = computed(() => {
      return props.disabled
        ? [
            sizeClass.value,
            'text-[color:var(--token-c-input-color-text-disabled)]',
            'placeholder:color-[color:var(--token-c-input-color-text-placeholder-disabled)]',
          ]
        : [
            sizeClass.value,
            'text-[color:var(--token-c-input-color-text-default)]',
            'placeholder:color-[color:var(--token-c-input-color-text-placeholder-default)]',
          ];
    });

    const showClearButton = computed(() => props.modelValue && !props.disabled);

    function focus() {
      inputRef.value?.focus();
    }

    function onClick(event) {
      focus();
      emit('click', event);
    }

    function onClickClear() {
      emit('input', null);
      emit('clear');
    }

    function onFocus(event) {
      isFocused.value = true;
      emit('focus', event);
    }

    function onBlur(event) {
      isFocused.value = false;
      emit('blur', event);
    }

    function onInput(event) {
      const value = event.currentTarget.value;
      emit('input', value);
    }

    return {
      focus,
      isFocused,
      inputClasses,
      wrapperClasses,
      showClearButton,
      onClick,
      onClickClear,
      onFocus,
      onBlur,
      onInput,
    };
  },
};
</script>

<template>
  <div class="border border-solid px-3 flex items-center gap-2 rounded-full" :class="wrapperClasses" @click="onClick">
    <div
      class="inline-flex items-center gap-2"
      :class="{
        'fill-[color:var(--token-c-input-color-placeholder)]': !disabled,
        'fill-[color:var(--token-c-input-color-placeholder-disabled)]': disabled,
      }"
    >
      <IconSearch />
    </div>
    <input
      ref="inputRef"
      class="w-full m-0 p-0 border-0 outline-none text-default leading-6 bg-transparent appearance-none"
      :class="inputClasses"
      :disabled="disabled"
      type="search"
      :value="modelValue"
      v-bind="$attrs"
      v-on="{ ...$listeners, input: onInput, blur: onBlur, focus: onFocus }"
    />
    <div
      v-if="showClear"
      class="inline-flex items-center gap-2"
      :class="{
        'fill-[color:var(--token-c-input-color-placeholder)]': !disabled,
        'fill-[color:var(--token-c-input-color-placeholder-disabled)]': disabled,
      }"
    >
      <button
        v-if="showClearButton"
        type="button"
        class="border-0 p-0 m-0 appearance-none outline-none inline-flex items-center justify-center bg-transparent"
        @click.stop="onClickClear"
      >
        <IconClear
          class="w-4 h-4"
          :class="{
            'fill-[color:var(--token-c-input-color-border-invalid)]': invalid,
          }"
        />
      </button>
    </div>
  </div>
</template>
